import { ListOrdered, Send, Skull, Workflow } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, type JobQueueSummary, type OpsQueues } from '../../api';
import { Badge } from '../../components/Badge';
import { DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { EnumBadge, RefreshButton, Since, WORKFLOW_TONE } from './shared';

type OutboxRow = OpsQueues['outbox'][number];
type WorkflowRow = OpsQueues['workflows'][number];

/** `/ops/queues`: scheduled actions, outbox and in-flight workflows (spec §104, §105). */
export function QueuesPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('queues.title'));
  const result = useApi((signal) => api.getOpsQueues({ signal }), []);

  const jobColumns: Column<JobQueueSummary>[] = [
    { key: 'queue', header: t('queues.col.queue'), cell: (q) => <code className="text-xs">{q.queue}</code> },
    { key: 'ready', header: t('queues.col.ready'), numeric: true, cell: (q) => formatCount(q.ready) },
    { key: 'running', header: t('queues.col.running'), numeric: true, cell: (q) => formatCount(q.running) },
    { key: 'scheduled', header: t('queues.col.scheduled'), numeric: true, cell: (q) => formatCount(q.scheduled) },
    { key: 'retrying', header: t('queues.col.retrying'), numeric: true, cell: (q) => (q.retrying ? <Badge tone="warn">{formatCount(q.retrying)}</Badge> : 0) },
    { key: 'dead', header: t('queues.col.dead'), numeric: true, cell: (q) => (q.dead ? <Badge tone="crit">{formatCount(q.dead)}</Badge> : 0) },
    { key: 'oldest', header: t('queues.col.oldestReady'), cell: (q) => <Since at={q.oldest_ready_at} /> },
    { key: 'attempts', header: t('queues.col.maxAttempts'), numeric: true, cell: (q) => q.max_attempts_seen ?? '—' },
  ];
  const outboxColumns: Column<OutboxRow>[] = [
    { key: 'type', header: t('queues.col.eventType'), cell: (o) => <code className="text-xs">{o.event_type}</code> },
    { key: 'pending', header: t('queues.col.pending'), numeric: true, cell: (o) => formatCount(o.pending) },
    { key: 'oldest', header: t('queues.col.oldest'), cell: (o) => <Since at={o.oldest_created_at} /> },
  ];
  const workflowColumns: Column<WorkflowRow>[] = [
    {
      key: 'type',
      header: t('queues.col.workflowType'),
      cell: (w) => (
        <Link to={`/ops/workflows?workflow_type=${encodeURIComponent(w.workflow_type)}&state=${encodeURIComponent(w.state.toUpperCase())}`} className="font-mono text-xs text-primary hover:underline">
          {w.workflow_type}
        </Link>
      ),
    },
    { key: 'state', header: t('queues.col.state'), cell: (w) => <EnumBadge group="workflowState" value={w.state} tones={WORKFLOW_TONE} /> },
    { key: 'count', header: t('queues.col.count'), numeric: true, cell: (w) => formatCount(w.count) },
    { key: 'oldest', header: t('queues.col.oldestInState'), cell: (w) => <Since at={w.oldest} /> },
  ];

  const d = result.status === 'success' ? result.data : null;
  const ready = d?.scheduled_actions.reduce((a, q) => a + q.ready, 0) ?? 0;
  const dead = d?.scheduled_actions.reduce((a, q) => a + q.dead, 0) ?? 0;
  const outbox = d?.outbox.reduce((a, o) => a + o.pending, 0) ?? 0;
  const workflows = d?.workflows.reduce((a, w) => a + w.count, 0) ?? 0;

  return (
    <>
      <PageHeader title={t('queues.title')} subtitle={t('queues.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      {result.status === 'loading' ? <Loading rows={4} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('queues.loadFailed')} /> : null}
      {d ? (
        <div className="space-y-6">
          <StatGrid>
            <StatCard icon={ListOrdered} label={t('queues.stat.ready')} value={formatCount(ready)} hint={t('queues.stat.readyHint')} />
            <StatCard icon={Skull} label={t('queues.stat.dead')} value={formatCount(dead)} tone={dead ? 'crit' : 'ok'} hint={t('queues.stat.deadHint')} />
            <StatCard icon={Send} label={t('queues.stat.outbox')} value={formatCount(outbox)} tone={outbox > 20 ? 'warn' : 'neutral'} />
            <StatCard icon={Workflow} label={t('queues.stat.workflows')} value={formatCount(workflows)} />
          </StatGrid>
          <Section title={t('queues.jobs.title')} flush>
            <DataTable columns={jobColumns} rows={d.scheduled_actions} rowKey={(q) => q.queue} empty={<EmptyState title={t('queues.jobs.empty')} />} />
          </Section>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Section title={t('queues.outbox.title')} flush>
              <DataTable columns={outboxColumns} rows={d.outbox} rowKey={(o) => o.event_type} empty={<EmptyState title={t('queues.outbox.empty')} />} />
            </Section>
            <Section title={t('queues.workflows.title')} flush>
              <DataTable columns={workflowColumns} rows={d.workflows} rowKey={(w) => `${w.workflow_type}/${w.state}`} empty={<EmptyState title={t('queues.workflows.empty')} />} />
            </Section>
          </div>
        </div>
      ) : null}
    </>
  );
}
